import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CalendarRange,
  CheckCircle2,
  Clock,
  Hourglass,
  PencilLine,
  RotateCcw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
} from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  DateRangePicker,
  EmptyState,
  InfoHint,
  PageHeader,
  BackLink,
  SegmentedControl,
  Select,
  SkeletonTable,
  Tabs,
  toast,
  type DataTableColumn,
  useReturnOrigin,
  withReturn,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { useAgreementDetail } from '../agreements/api';
import { formatDate, formatHours, formatMoney } from '../../lib/format';
import {
  useCreateInvoice,
  useInvoiceClients,
  useUnbilled,
  useUnbilledPreview,
  type UnbilledClientRow,
  type UnbilledGrouping,
  type UnbilledPreview,
} from './api';
import {
  LineItemsEditor,
  MetaFields,
  defaultMeta,
  emptyLine,
  linesSubtotal,
  metaDiscount,
  metaTax,
  newLineKey,
  suggestSubject,
  todayIso,
  type EditableLine,
  type InvoiceMeta,
} from './line-editor';
import { InstallmentsTab } from './new-invoice/InstallmentsTab';
import { PrepaidHoursTab } from './new-invoice/PrepaidHoursTab';
import { invoiceListHref } from './list-view';

/**
 * How the wizard rolls unbilled hours into lines. Harvest asks before it
 * builds the draft; asking afterwards means deleting ten lines by hand to get
 * a summary invoice. Ordered coarse → fine, which is also cheapest → most
 * detailed for the client reading it.
 */
const GROUPING_OPTIONS: Array<{ value: UnbilledGrouping; label: string; blurb: string }> = [
  {
    value: 'agreement',
    label: 'Agreement',
    blurb: 'One line per agreement — the summary invoice.',
  },
  {
    value: 'task',
    label: 'Agreement + task',
    blurb: 'A line per task within each agreement.',
  },
  {
    value: 'person',
    label: '+ person',
    blurb: 'A line per person on each task — who did what.',
  },
  {
    value: 'entry',
    label: 'Itemized',
    blurb: 'One line per time entry, dated. Long, and impossible to argue with.',
  },
];

function firstOfPreviousMonth(): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1));
  return d.toISOString().slice(0, 10);
}

/** The first and last day of the month `offset` months from this one. */
function monthRange(offset: number): { from: string; to: string } {
  const [y, m] = todayIso().split('-').map(Number);
  const index = y * 12 + (m - 1) + offset;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mm = String(month).padStart(2, '0');
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, '0')}` };
}

/**
 * Which work the Time & materials list covers. Jesse, 2026-09-30: *"we do
 * need to create some T&M invoices, but we would only be looking at a month
 * of time (or a custom date range)"* — so it opens on LAST month, the one a
 * T&M invoice is normally for; "All unbilled" is still one click away.
 */
type BillingPeriod = 'last' | 'this' | 'all' | 'custom';
const PERIOD_OPTIONS: Array<{ value: BillingPeriod; label: string }> = [
  { value: 'last', label: 'Last month' },
  { value: 'this', label: 'This month' },
  { value: 'custom', label: 'Custom' },
  { value: 'all', label: 'All unbilled' },
];
const RANGE_PRESETS = [
  { label: 'Last month', range: () => monthRange(-1) },
  { label: 'This month', range: () => ({ from: monthRange(0).from, to: todayIso() }) },
];

/** Days between two YYYY-MM-DD dates. */
function daysSince(iso: string): number {
  const then = new Date(`${iso}T12:00:00Z`).getTime();
  const now = new Date(`${todayIso()}T12:00:00Z`).getTime();
  return Math.max(0, Math.round((now - then) / 86_400_000));
}

// ---------------------------------------------------------------------------
// Shared create logic
// ---------------------------------------------------------------------------

/**
 * ⚠ AN ERROR BELONGS TO THE CONTROL THAT PRODUCED IT.
 *
 * "Pick a client first." used to render at the bottom-LEFT of the card while
 * "Create draft invoice" sat bottom-right and the client Select was roughly a
 * thousand pixels north of both — so the sentence naming the problem, the
 * button that raised it and the field that fixes it were three separate places
 * on the screen. `field` is what lets the message be shown ON the control (and
 * the control focused) instead of only in a footnote.
 */
type DraftField = 'client' | 'lines' | null;

function useCreateDraft() {
  // A draft raised inside a client goes back to that client, not to the
  // portfolio the reader was never looking at.
  const from = useReturnOrigin();
  const navigate = useNavigate();
  const create = useCreateInvoice();
  const [error, setError] = useState<string | null>(null);
  const [field, setField] = useState<DraftField>(null);
  const [badKeys, setBadKeys] = useState<Set<string>>(() => new Set());

  const fail = (message: string, which: DraftField, keys: string[] = []) => {
    setError(message);
    setField(which);
    setBadKeys(new Set(keys));
    // Take the user to the thing they must change. A message alone still
    // leaves them hunting for it on a long form.
    if (which) {
      window.requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-draft-field="${which}"]`);
        el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el?.querySelector<HTMLElement>('select,input,textarea')?.focus();
      });
    }
  };

  const submit = (args: {
    clientId: string;
    lines: EditableLine[];
    meta: InvoiceMeta;
    fromUnbilled: boolean;
  }) => {
    const { clientId, lines, meta, fromUnbilled } = args;
    if (!clientId) {
      fail('Pick a client first.', 'client');
      return;
    }
    const usable = lines.filter((l) => l.description.trim() && lineAmountOk(l));
    if (usable.length === 0) {
      fail('Every line needs a description and a positive quantity.', 'lines', [lines[0]?.key].filter(Boolean) as string[]);
      return;
    }
    const noQty = usable.filter((l) => !Number.isFinite(Number(l.quantity)) || Number(l.quantity) <= 0);
    if (noQty.length > 0) {
      fail(
        `Line ${lines.indexOf(noQty[0]) + 1} needs a positive quantity.`,
        'lines',
        noQty.map((l) => l.key),
      );
      return;
    }
    setError(null);
    setField(null);
    setBadKeys(new Set());
    create.mutate(
      {
        fromUnbilled,
        clientId,
        issueDate: meta.issueDate,
        dueDate: meta.dueDate,
        notes: meta.notes.trim() || undefined,
        // ⚠ THE PLACEHOLDER IS THE DEFAULT, NOT A DIFFERENT ONE. Leaving this
        // blank used to save nothing, and every screen then fell back to the
        // first line's raw description — so the form showed "Redwood — Aug
        // 2026" and the client's PDF was headed "Redwood — Support — 3.5 hrs @
        // $150.00/hr — August 2026". A field must save what it offered.
        subject: meta.subject.trim() || suggestSubject(usable, meta.issueDate),
        poNumber: meta.poNumber.trim() || undefined,
        discountAmount: metaDiscount(meta, linesSubtotal(usable)),
        // Tax follows the discount — the server computes the total the same way.
        taxAmount: metaTax(
          meta,
          linesSubtotal(usable) - metaDiscount(meta, linesSubtotal(usable)),
        ),
        lines: usable.map((l) => ({
          description: l.description.trim(),
          agreementId: l.agreementId,
          quantity: Number(l.quantity),
          unitPrice: Number(l.unitPrice) || 0,
          kind: l.kind,
          timeEntryIds: l.timeEntryIds.length > 0 ? l.timeEntryIds : undefined,
          expenseIds: l.expenseIds.length > 0 ? l.expenseIds : undefined,
        })),
      },
      {
        onSuccess: (inv) => {
          const locked = inv.linkedTimeEntryCount + inv.linkedExpenseCount;
          toast.success(
            locked > 0
              ? `Draft #${inv.number} created — ${inv.linkedTimeEntryCount} time entries and ${inv.linkedExpenseCount} expenses locked to it`
              : `Draft #${inv.number} created`,
          );
          navigate(withReturn(`/invoices/${inv.id}`, from));
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'Could not create the invoice'),
      },
    );
  };

  return { submit, pending: create.isPending, error, field, badKeys, setError, setBadKeys };
}

function lineAmountOk(l: EditableLine): boolean {
  const qty = Number(l.quantity);
  const rate = Number(l.unitPrice);
  return Number.isFinite(qty) && Number.isFinite(rate) && qty * rate >= 0;
}

// ---------------------------------------------------------------------------
// Path 1: from unbilled time — "Ready to bill" index, then the line editor
// ---------------------------------------------------------------------------

/**
 * WHAT THIS ROW IS NOT OFFERING, AND WHY — one line, under the client's name.
 *
 * The wizard used to price every uninvoiced billable hour it could find. Two
 * kinds of hour are not money, and printing them as money is how this screen
 * came to say "$934,719 of unbilled work" while /clients said "$34,478" about
 * the same second of the same data:
 *
 *  · **Prepaid support.** The client bought the hours in advance. Invoicing
 *    them charges twice for one hour — and the client page states in writing
 *    that we exclude them from every unbilled figure.
 *  · **Fee work.** A retainer or a fixed bid earns its fee; its tracked hours
 *    are cost. "Hours × rate is never printed as money on a non-hourly
 *    agreement" is this app's own rule, and this screen was the one place
 *    breaking it — $754,206 worth, on six agreements.
 *
 * Neither is hidden. Work that happened must not vanish because it is not
 * billable; it is named, counted, and left out of the amount.
 */
function NotBillable({ row }: { row: UnbilledClientRow }) {
  if (row.prepaidHours <= 0 && row.feeHours <= 0) return null;
  const feeNames = row.feeAgreements
    .slice(0, 2)
    .map((p) => `${p.name} (${p.noun})`)
    .join(', ');
  return (
    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-caption text-grey-500">
      {row.prepaidHours > 0 && (
        <span
          className="num whitespace-nowrap"
          title={`${formatHours(row.prepaidHours, { unit: true })} of uninvoiced support on this client has already been paid for out of a prepaid block (${formatMoney(
            row.prepaidAmount,
            { whole: true },
          )} at the bill rate). It is excluded here and from /clients — invoicing it would charge twice for the same hour.`}
        >
          <ShieldCheck className="mr-1 inline h-3 w-3 shrink-0 align-[-2px]" aria-hidden />
          {formatHours(row.prepaidHours, { fixed: true })} h prepaid — already paid for
        </span>
      )}
      {row.feeHours > 0 && (
        <span
          className="num whitespace-nowrap"
          title={`${formatHours(row.feeHours, { unit: true })} tracked on ${feeNames}${
            row.feeAgreements.length > 2 ? ` and ${row.feeAgreements.length - 2} more` : ''
          }. Those agreements bill a fee, not an hour, so their time is delivery cost — there is no hourly line to compose from it. Invoice the fee itself, or set the agreement up as an hourly one.`}
        >
          {formatHours(row.feeHours, { fixed: true })} h fee work — billed by its fee
        </span>
      )}
    </p>
  );
}

/**
 * THE PROMISE, KEPT ON THE SCREEN THAT CREATES THE INVOICE.
 *
 * A client page that says "excluded from EVERY unbilled figure — invoicing it
 * would charge twice for the same hour" and a Create-draft button two clicks
 * away that bills it anyway is worse than never making the promise. The server
 * now refuses both kinds of hour; this states the refusal where the money is
 * about to be committed, with the figures, so nobody has to wonder where the
 * hours went.
 */
function ExcludedFromDraft({
  excluded,
  clientName,
}: {
  excluded: UnbilledPreview['excluded'];
  clientName: string;
}) {
  const { prepaid, fee } = excluded;
  if (prepaid.hours <= 0 && fee.hours <= 0) return null;
  return (
    <CardBody className="border-b border-grey-200 pt-0">
      <div className="rounded-lg border border-grey-200 bg-grey-50 px-3 py-2.5">
        <p className="flex items-center gap-1.5 text-caption font-bold uppercase tracking-wider text-grey-500">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Left off this draft on purpose
        </p>
        <ul className="mt-1.5 space-y-1 text-body-sm text-grey-600">
          {prepaid.hours > 0 && (
            <li>
              <span className="num font-semibold text-secondary">
                {formatHours(prepaid.hours, { unit: true })}
              </span>{' '}
              of support work
              {prepaid.amount > 0 && (
                <>
                  {' '}
                  (
                  <span className="num">{formatMoney(prepaid.amount, { whole: true })}</span> at the
                  bill rate)
                </>
              )}{' '}
              has already been paid for out of the prepaid block for {clientName}. Billing it would
              charge twice for the same hour.
            </li>
          )}
          {fee.hours > 0 && (
            <li>
              <span className="num font-semibold text-secondary">
                {formatHours(fee.hours, { unit: true })}
              </span>{' '}
              is on{' '}
              {fee.agreements
                .slice(0, 3)
                .map((p) => `${p.name} (${p.noun})`)
                .join(', ')}
              {fee.agreements.length > 3 && ` and ${fee.agreements.length - 3} more`} — those bill a fee,
              not an hour, so the time is delivery cost. Add the fee as a line of its own.
            </li>
          )}
        </ul>
      </div>
    </CardBody>
  );
}

/**
 * WHY THE BUTTON ON THIS ROW IS OFF.
 *
 * Five of the fourteen rows here are $0 — they exist to explain a zero, not
 * to offer one (a client whose uninvoiced work is all prepaid support, or all
 * fee work). They still carried a live "Draft invoice →" that produced an
 * empty draft: an invoice for nothing, on a real client, one click from a
 * screen that had just said in words there is nothing to bill.
 *
 * Disabled, never hidden — the same rule Export CSV follows at zero rows: a
 * control that vanishes reads as a missing feature. The reason is on the
 * control, because a refusal a reader cannot account for is worse than none.
 */
function nothingToBillReason(row: UnbilledClientRow): string {
  const parts = [
    row.prepaidHours > 0
      ? `${formatHours(row.prepaidHours, { unit: true })} of support already paid for out of a prepaid block`
      : '',
    row.feeHours > 0
      ? `${formatHours(row.feeHours, { unit: true })} of fee work, billed by its fee`
      : '',
  ].filter(Boolean);
  if (parts.length === 0) {
    return `Nothing billable for ${row.clientName} in this range. Use Manual to build an invoice by hand.`;
  }
  return `Nothing billable for ${row.clientName} in this range — ${parts.join(
    ' and ',
  )}. Neither is money anybody owes. Use Manual to build an invoice by hand.`;
}

function ReadyToBillTable({
  rows,
  onDraft,
  activeClientId,
}: {
  rows: UnbilledClientRow[];
  onDraft: (row: UnbilledClientRow) => void;
  activeClientId: string;
}) {
  const columns = useMemo<Array<DataTableColumn<UnbilledClientRow>>>(
    () => [
      {
        key: 'client',
        header: 'Client',
        sortable: true,
        sortValue: (r) => r.clientName,
        render: (r) => (
          <div className="min-w-0">
            <p className="font-semibold text-secondary">{r.clientName}</p>
            <p className="truncate text-caption text-grey-500">
              {r.agreements
                .slice(0, 2)
                .map((p) => p.name)
                .join(' · ')}
              {r.agreements.length > 2 && ` · +${r.agreements.length - 2} more`}
              {r.agreements.length === 0 && 'Nothing to bill in this range'}
            </p>
            <NotBillable row={r} />
          </div>
        ),
        className: 'max-w-[20rem]',
      },
      {
        key: 'hours',
        header: 'Unbilled hours',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.hours,
        // `fixed` so a right-aligned stack reads 1,913.50 / 1,349.00 / 897.75
        // instead of a ragged 1,913.5 / 1,349 / 897.75.
        render: (r) => (
          <span className="whitespace-nowrap">
            {formatHours(r.hours, { fixed: true })}
            <span className="ml-1 text-caption text-grey-400">h</span>
          </span>
        ),
        className: 'hidden sm:table-cell w-40 whitespace-nowrap',
      },
      {
        key: 'entries',
        header: 'Items',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.entryCount + r.expenseCount,
        render: (r) => (
          <span className="whitespace-nowrap text-grey-500">
            {r.entryCount}
            {r.expenseCount > 0 && ` + ${r.expenseCount} exp`}
          </span>
        ),
        className: 'hidden 2xl:table-cell w-28',
      },
      {
        key: 'oldest',
        header: 'Oldest work',
        sortable: true,
        sortValue: (r) => r.oldestEntryDate ?? '',
        render: (r) =>
          r.oldestEntryDate ? (
            <span className="num whitespace-nowrap">
              {formatDate(r.oldestEntryDate)}
              <span
                className={cn(
                  'ml-1.5 text-caption',
                  daysSince(r.oldestEntryDate) > 60 ? 'font-semibold text-status-late' : 'text-grey-400',
                )}
              >
                ({daysSince(r.oldestEntryDate)}d)
              </span>
            </span>
          ) : (
            <span className="text-grey-400">—</span>
          ),
        className: 'hidden md:table-cell w-44',
      },
      {
        key: 'amount',
        header: 'Unbilled amount',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.amount,
        render: (r) => <span className="font-semibold text-secondary">{formatMoney(r.amount)}</span>,
        className: 'w-44 whitespace-nowrap',
      },
      {
        key: 'action',
        header: '',
        align: 'right',
        render: (r) => (
          <Button
            size="sm"
            variant={activeClientId === r.clientId ? 'accent' : 'outline'}
            disabled={r.amount <= 0}
            title={r.amount <= 0 ? nothingToBillReason(r) : undefined}
            onClick={(e) => {
              e.stopPropagation();
              onDraft(r);
            }}
          >
            Draft invoice
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Button>
        ),
        className: 'w-36',
      },
    ],
    [onDraft, activeClientId],
  );

  const totalAmount = rows.reduce((s, r) => s + r.amount, 0);
  const totalHours = rows.reduce((s, r) => s + r.hours, 0);
  /**
   * ⚠ A TOTAL ACROSS A SET IS A ROLLUP, AND A ROLLUP IS WHOLE DOLLARS.
   * This footer printed "$38,791.00" under a card header reading "$38,791 to
   * bill across 9 clients" — the same figure, four inches apart, gaining
   * cents on the way down. Rows keep their cents (each one is what a single
   * client's draft would come to); the footing follows /invoices, where the
   * exact figure is one hover away rather than absent.
   */
  const exactTotal = `Exactly ${formatMoney(totalAmount)} — rounded to whole dollars, like every rollup in this app`;
  // ⚠ A COUNT ON SCREEN MUST ADD UP AGAINST THE LIST BESIDE IT. Some rows are
  // here to explain a zero — a client whose uninvoiced work is all prepaid, or
  // all on fee agreements — so "14 clients ready to bill" over a table with six
  // $0 rows would be wrong twice over.
  const billable = rows.filter((r) => r.amount > 0).length;
  const nothing = rows.length - billable;
  const readyLabel = (plural: boolean) =>
    `${billable} client${billable === 1 ? '' : 's'} ready to bill${
      nothing > 0 ? ` · ${nothing} with nothing billable` : ''
    }` + (plural ? '' : '');

  return (
    <>
      {/* Phones: the columns would scroll the money off-screen, so stack. */}
      <div className="sm:hidden">
        {[...rows]
          .sort((a, b) => b.amount - a.amount)
          .map((r) => (
            <div key={r.clientId} className="border-b border-grey-100 px-4 py-3 last:border-b-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-secondary">{r.clientName}</p>
                  <p className="num mt-0.5 text-caption text-grey-500">
                    {formatHours(r.hours, { unit: true })} · {r.entryCount} items
                    {r.oldestEntryDate && ` · oldest ${formatDate(r.oldestEntryDate)}`}
                  </p>
                  <NotBillable row={r} />
                </div>
                <p className="num shrink-0 text-body font-bold text-secondary">
                  {formatMoney(r.amount)}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-2 w-full"
                disabled={r.amount <= 0}
                title={r.amount <= 0 ? nothingToBillReason(r) : undefined}
                onClick={() => onDraft(r)}
              >
                Draft invoice
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Button>
            </div>
          ))}
        <div className="flex items-center justify-between gap-2 border-t border-grey-200 bg-grey-50 px-4 py-2.5">
          <span className="text-caption font-bold uppercase tracking-wider text-grey-500">
            {readyLabel(true)}
          </span>
          <span className="num text-body-sm font-bold text-secondary" title={exactTotal}>
            {formatMoney(totalAmount, { whole: true })}
          </span>
        </div>
      </div>
      <div className="hidden sm:block">
        <DataTable
          columns={columns}
          data={rows}
          rowKey={(r) => r.clientId}
          onRowClick={(r) => {
            if (r.amount > 0) onDraft(r);
          }}
          defaultSort={{ key: 'amount', dir: 'desc' }}
          footer={
            <tr className="border-t-2 border-grey-200">
              <td className="px-4 py-2.5 text-caption font-bold uppercase tracking-wider text-grey-500">
                {readyLabel(true)}
              </td>
              <td className="num-cell hidden px-4 py-2.5 sm:table-cell">
                {/* Same precision AND the same unit as the column it foots:
                    the rows read "47.00 h" and this read a bare "205.25". */}
                {formatHours(totalHours, { fixed: true })}
                <span className="ml-1 text-caption text-grey-400">h</span>
              </td>
              <td className="hidden px-4 py-2.5 2xl:table-cell" />
              <td className="hidden px-4 py-2.5 md:table-cell" />
              <td className="num-cell px-4 py-2.5 text-secondary" title={exactTotal}>
                {formatMoney(totalAmount, { whole: true })}
              </td>
              <td className="px-4 py-2.5" />
            </tr>
          }
        />
      </div>
    </>
  );
}

function FromUnbilledTab() {
  const clients = useInvoiceClients();
  const [period, setPeriod] = useState<BillingPeriod>('last');
  const [customRange, setCustomRange] = useState(() => monthRange(-1));
  const periodRange =
    period === 'all'
      ? null
      : period === 'custom'
        ? customRange
        : period === 'this'
          ? { from: monthRange(0).from, to: todayIso() }
          : monthRange(-1);
  const unbilled = useUnbilled(periodRange ?? undefined);
  const preview = useUnbilledPreview();
  const draft = useCreateDraft();
  const editorRef = useRef<HTMLDivElement>(null);
  // Deep link from an agreement's "Invoice $X unbilled" button:
  // /invoices/new?clientId=…&from=…&to=…&source=<agreement name>
  const [searchParams] = useSearchParams();
  const prefilled = useRef(false);
  const sourceLabel = searchParams.get('source');

  const [clientId, setClientId] = useState('');
  const [range, setRange] = useState(() => monthRange(-1));
  const [showManualPicker, setShowManualPicker] = useState(false);
  const [lines, setLines] = useState<EditableLine[] | null>(null);
  const [loaded, setLoaded] = useState<UnbilledPreview | null>(null);
  const [groupBy, setGroupBy] = useState<UnbilledGrouping>('task');
  const [meta, setMeta] = useState<InvoiceMeta>(() => defaultMeta());

  const load = (args: { clientId: string; from: string; to: string; groupBy?: UnbilledGrouping }) => {
    if (!args.clientId || !args.from || !args.to) return;
    const grouping = args.groupBy ?? groupBy;
    setClientId(args.clientId);
    setRange({ from: args.from, to: args.to });
    setGroupBy(grouping);
    preview.mutate({ ...args, groupBy: grouping }, {
      onSuccess: (data) => {
        setLoaded(data);
        setLines(
          data.lines.map((l) => ({
            key: newLineKey(),
            description: l.description,
            detail: l.detail,
            agreementId: l.agreementId,
            quantity: String(l.quantity),
            // 2 dp from the start, so a generated rate matches the AMOUNT
            // column it multiplies into.
            unitPrice: l.unitPrice.toFixed(2),
            kind: l.kind,
            timeEntryIds: l.timeEntryIds,
            expenseIds: l.expenseIds ?? [],
            source: l.source,
            entries: l.entries ?? [],
          })),
        );
      },
    });
  };

  // Run the preview once for a deep link so the caller lands on Step 2 with
  // the lines already built, not on a 14-row client index they have to re-find
  // themselves in.
  useEffect(() => {
    if (prefilled.current) return;
    const clientId = searchParams.get('clientId');
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    if (!clientId || !from || !to) return;
    // Fire after the mount settles rather than inside it: React 18 StrictMode
    // mounts → unmounts → remounts in dev, and react-query silently drops the
    // per-call onSuccess of a mutation whose observer unmounted mid-flight —
    // the request 200s and nothing renders. The frame guard puts the call on
    // the surviving mount, so the deep link behaves the same in dev and prod.
    const frame = requestAnimationFrame(() => {
      prefilled.current = true;
      load({ clientId, from, to });
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /** Back to the index — Step 1 expands again and Step 2 is discarded. */
  const changeClient = () => {
    setLoaded(null);
    setLines(null);
    setClientId('');
    setShowManualPicker(false);
  };

  // Step 1 collapses to a one-line summary the moment a client is chosen, so
  // Step 2 is what the click actually lands you on — not a 14-row table.
  const collapsed = loaded !== null && lines !== null;

  // Scroll AFTER the collapse has committed; scrolling inside the mutation
  // callback lands on the pre-collapse layout (i.e. still on Step 1).
  useEffect(() => {
    if (!collapsed) return;
    const frame = requestAnimationFrame(() =>
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
    return () => cancelAnimationFrame(frame);
  }, [collapsed, loaded]);

  const draftFromRow = (row: UnbilledClientRow) =>
    load({
      clientId: row.clientId,
      // Exactly the period the row was counted over — a month's T&M invoice
      // bills that month. "All unbilled" covers every hour the row promises.
      from: periodRange?.from || row.oldestEntryDate || firstOfPreviousMonth(),
      to: periodRange?.to || unbilled.data?.to || todayIso(),
    });

  const clientName = useMemo(
    () => loaded?.client.name ?? clients.data?.find((c) => c.id === clientId)?.name,
    [loaded, clients.data, clientId],
  );

  const rows = unbilled.data?.clients ?? [];
  const totals = unbilled.data?.totals;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="1 · Ready to bill"
          /* ⚠ A LABEL, NOT A LESSON (Jesse, 2026-08-13). The subtitle carried
             the figures AND the cross-screen reconciliation with /clients AND
             the two exclusions — three lines above the list you are here to
             pick from. The figures stay; the reconciliation is behind the (i). */
          subtitle={
            collapsed ? undefined : totals ? (
              <span className="inline-flex flex-wrap items-center gap-x-1.5">
                <span>
                  {formatMoney(totals.amount, { whole: true })} to bill across{' '}
                  {totals.clientCount} client{totals.clientCount === 1 ? '' : 's'} —{' '}
                  {formatHours(totals.hours, { unit: true })} and {totals.expenseCount} billable
                  expense{totals.expenseCount === 1 ? '' : 's'},{' '}
                  {unbilled.data?.from
                    ? `${formatDate(unbilled.data.from)} – ${formatDate(unbilled.data.to)}`
                    : `through ${formatDate(unbilled.data?.to ?? todayIso())}`}
                </span>
                <InfoHint label="What this total counts" width={330}>
                  <p>
                    Hours <strong>and</strong> billable expenses — the same total{' '}
                    <Link to="/clients" className="font-semibold text-accent hover:underline">
                      /clients
                    </Link>{' '}
                    prints as <strong>Unbilled</strong>, to the cent.
                  </p>
                  {(totals.prepaidHours > 0 || totals.feeHours > 0) && (
                    <p className="mt-2">
                      It excludes{' '}
                      {[
                        totals.prepaidHours > 0
                          ? `${formatHours(totals.prepaidHours, { unit: true })} already paid for out of prepaid blocks`
                          : '',
                        totals.feeHours > 0
                          ? `${formatHours(totals.feeHours, { unit: true })} of fee work`
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' and ')}
                      {' '}— neither of which is money anybody owes.
                    </p>
                  )}
                </InfoHint>
              </span>
            ) : (
              'Every client with billable work that has never been put on an invoice.'
            )
          }
          actions={
            collapsed ? (
              <Button size="sm" variant="outline" onClick={changeClient}>
                <ArrowLeft className="h-4 w-4" aria-hidden />
                Change client
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => unbilled.refetch()}
                loading={unbilled.isFetching}
              >
                <RotateCcw className="h-4 w-4" aria-hidden />
                Refresh
              </Button>
            )
          }
        />
        {!collapsed && (
          <CardBody className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-4 pt-0">
            <span className="text-caption font-bold uppercase tracking-wider text-grey-500">Work dated</span>
            <SegmentedControl
              ariaLabel="Which work to bill"
              size="sm"
              scrollable
              value={period}
              onChange={setPeriod}
              options={PERIOD_OPTIONS}
            />
            {period === 'custom' && (
              <DateRangePicker value={customRange} onChange={setCustomRange} presets={RANGE_PRESETS} />
            )}
          </CardBody>
        )}
        {collapsed && loaded ? (
          <CardBody className="pt-0">
            <p className="text-body font-semibold text-secondary">
              {loaded.client.name}
              <span className="num font-normal text-grey-500">
                {' · '}
                {formatHours(loaded.totals.hours, { unit: true })}
                {loaded.totals.expenseCount > 0 &&
                  ` + ${loaded.totals.expenseCount} expense${loaded.totals.expenseCount === 1 ? '' : 's'}`}
                {' · '}
              </span>
              <span className="num">{formatMoney(loaded.totals.amount)}</span>
            </p>
            {sourceLabel && (
              <p className="mt-1 text-caption text-grey-500">
                Opened from <span className="font-semibold text-grey-600">{sourceLabel}</span> — an
                invoice covers the whole client, so every unbilled item for {loaded.client.name} in
                this range is included. Remove any line you do not want to bill.
              </p>
            )}
            <p className="num mt-0.5 text-caption text-grey-500">
              {formatDate(loaded.from)} – {formatDate(loaded.to)} ·{' '}
              {rows.length > 1
                ? `${rows.length - 1} other client${rows.length === 2 ? '' : 's'} still ready to bill`
                : 'the only client ready to bill'}
            </p>
          </CardBody>
        ) : unbilled.isLoading ? (
          <SkeletonTable rows={6} />
        ) : unbilled.isError ? (
          <EmptyState
            icon={<Clock />}
            title="Could not load unbilled work"
            description={
              unbilled.error instanceof Error ? unbilled.error.message : 'Something went wrong.'
            }
            action={
              <Button variant="outline" onClick={() => unbilled.refetch()}>
                Try again
              </Button>
            }
          />
        ) : rows.length === 0 ? (
          periodRange ? (
            <EmptyState
              icon={<CheckCircle2 />}
              title="Nothing unbilled in this period"
              description={`No client has billable time or expenses dated ${formatDate(periodRange.from)} – ${formatDate(periodRange.to)} waiting for an invoice.`}
              action={
                <Button variant="outline" onClick={() => setPeriod('all')}>
                  Show all unbilled
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<CheckCircle2 />}
              title="Everything is billed"
              description="No client has billable time or expenses waiting for an invoice. Track more time, or build an invoice by hand."
              action={
                <Button variant="outline" onClick={() => setShowManualPicker(true)}>
                  Pick a client and range
                </Button>
              }
            />
          )
        ) : (
          <ReadyToBillTable rows={rows} onDraft={draftFromRow} activeClientId={clientId} />
        )}

        {/* Secondary control: the old manual client + range picker. Hidden once
            a client is chosen — "Change client" brings the whole index back. */}
        <CardBody className={cn('border-t border-grey-200 pt-4', collapsed && 'hidden')}>
          {showManualPicker ? (
            <>
              <div className="flex flex-wrap items-end gap-4">
                <Select
                  label="Client"
                  className="w-64"
                  value={clientId}
                  onChange={(e) => {
                    setClientId(e.target.value);
                    setLines(null);
                    setLoaded(null);
                  }}
                  placeholder="Choose a client…"
                  options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
                />
                <DateRangePicker label="Work dates" value={range} onChange={setRange} presets={RANGE_PRESETS} />
                <Button
                  variant="outline"
                  onClick={() => load({ clientId, from: range.from, to: range.to })}
                  disabled={!clientId || !range.from || !range.to}
                  loading={preview.isPending}
                >
                  <Search className="h-4 w-4" aria-hidden />
                  Load unbilled work
                </Button>
              </div>
              <p className="mt-2 text-caption text-grey-500">
                Narrows the wizard to one client and a specific date range — useful for billing a
                single month.
              </p>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setShowManualPicker(true)}
              className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-grey-500 transition-colors hover:text-secondary"
            >
              <SlidersHorizontal className="h-4 w-4" aria-hidden />
              Or pick a client and date range yourself
            </button>
          )}
          {preview.isError && (
            <p className="mt-3 text-body-sm font-medium text-error">
              {preview.error instanceof Error ? preview.error.message : 'Could not load unbilled work.'}
            </p>
          )}
        </CardBody>
      </Card>

      {/* Offset clears the sticky topbar so Step 2's heading is not scrolled
          under it when the wizard jumps here. */}
      <div ref={editorRef} className="scroll-mt-24">
        {lines !== null && loaded && (
          <Card>
            <CardHeader
              title="2 · Review draft line items"
              subtitle={
                <>
                  {clientName} · {formatDate(loaded.from)} – {formatDate(loaded.to)} ·{' '}
                  <span className="num">
                    {formatHours(loaded.totals.hours, { unit: true })} across {loaded.totals.entryCount} entries
                    {loaded.totals.expenseCount > 0 && ` + ${loaded.totals.expenseCount} expenses`}
                  </span>
                </>
              }
            />
            {/* Choose the shape BEFORE editing. Rebuilding the draft is one
                request; deleting nine lines by hand to get a summary invoice
                is not a feature. */}
            <CardBody className="border-b border-grey-200 pb-4 pt-0">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="text-caption font-bold uppercase tracking-wider text-grey-500">
                  Group hours by
                </span>
                <SegmentedControl
                  ariaLabel="Group unbilled hours by"
                  size="sm"
                  scrollable
                  value={groupBy}
                  onChange={(value) =>
                    load({ clientId, from: range.from, to: range.to, groupBy: value })
                  }
                  options={GROUPING_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                />
                <span className="num text-caption text-grey-500">
                  {lines.length} line{lines.length === 1 ? '' : 's'}
                  {preview.isPending && ' · rebuilding…'}
                </span>
              </div>
              <p className="mt-1.5 text-caption text-grey-500">
                {GROUPING_OPTIONS.find((o) => o.value === groupBy)?.blurb}
                {loaded.rateSplits > 0 && (
                  <>
                    {' '}
                    <span className="text-secondary">
                      {loaded.rateSplits} extra line{loaded.rateSplits === 1 ? '' : 's'} here because
                      the same work was billed at more than one rate — a line's amount is its hours
                      × its rate, so rates cannot be blended away.
                    </span>
                  </>
                )}{' '}
                Rebuilding replaces any edits you have made to these lines.
              </p>
            </CardBody>
            <ExcludedFromDraft excluded={loaded.excluded} clientName={loaded.client.name} />
            {lines.length === 0 ? (
              <EmptyState
                icon={<Clock />}
                title="Nothing to bill in this range"
                description={
                  loaded.excluded.prepaid.hours > 0 || loaded.excluded.fee.hours > 0
                    ? 'Everything in this range is either already invoiced, already paid for out of a prepaid block, or on an agreement that bills a fee rather than an hour — see above.'
                    : 'Every billable hour for this client and range is already on an invoice. Try a wider range.'
                }
              />
            ) : (
              <>
                <LineItemsEditor
                  lines={lines}
                  onChange={(next) => {
                    setLines(next);
                    if (draft.badKeys.size > 0) draft.setBadKeys(new Set());
                  }}
                  allowAdd
                  meta={meta}
                  invalidKeys={draft.badKeys}
                />
                <CardBody className="border-t border-grey-200 pt-4">
                  <MetaFields meta={meta} onChange={setMeta} subtotal={linesSubtotal(lines)} lines={lines} />
                  <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
                    {draft.error && (
                      <p
                        role="alert"
                        className="mr-auto flex items-center gap-1.5 text-body-sm font-semibold text-error"
                      >
                        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                        {draft.error}
                      </p>
                    )}
                    <Button
                      size="lg"
                      loading={draft.pending}
                      onClick={() => draft.submit({ clientId, lines, meta, fromUnbilled: true })}
                    >
                      Create draft invoice
                    </Button>
                  </div>
                </CardBody>
              </>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Path 2: manual
// ---------------------------------------------------------------------------

/** The invoice `kind` an agreement type implies — the client-side mirror of
 *  `invoices/invoice-kind.ts#invoiceKindForAgreementType` (the server derives
 *  it too; this only makes the seeded line READ right before it is saved). */
function kindForAgreementType(type: string): string {
  switch (type) {
    case 'SUPPORT':
      return 'support';
    case 'RETAINER':
      return 'retainer';
    case 'HOSTING':
      return 'hosting';
    default:
      return 'service';
  }
}

function ManualTab() {
  const clients = useInvoiceClients();
  const draft = useCreateDraft();
  const [clientId, setClientId] = useState('');
  const [lines, setLines] = useState<EditableLine[]>([emptyLine()]);
  const [meta, setMeta] = useState<InvoiceMeta>(() => defaultMeta());

  /**
   * Deep link from an agreement's "New invoice" button:
   * /invoices/new?tab=manual&clientId=…&agreementId=…
   *
   * Jesse, 2026-09-10: "we need to be able to create invoices for agreements…
   * and it should prefill the client's hourly rate." The client is chosen and
   * the first line is seeded FROM the agreement: its name, its kind, and its
   * rate — the hourly rate for an hourly agreement, the contract value for a
   * fixed fee. Quantity stays 1 for the person to change; nothing here is
   * saved until "Create draft invoice".
   */
  const [searchParams] = useSearchParams();
  const linkedClientId = searchParams.get('clientId');
  const linkedAgreementId = searchParams.get('agreementId');
  const agreementDetail = useAgreementDetail(linkedAgreementId ?? undefined);
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    if (linkedClientId && !clientId) setClientId(linkedClientId);
    const a = agreementDetail.data?.agreement;
    if (!linkedAgreementId || !a) return;
    seeded.current = true;
    setClientId(a.client.id);
    const rate =
      a.billBy === 'FIXED' && a.budgetAmount !== null
        ? a.budgetAmount
        : a.hourlyRate;
    const kind = a.billBy === 'FIXED' && a.type === 'PROJECT' ? 'fee' : kindForAgreementType(a.type);
    setLines([
      {
        ...emptyLine(),
        description: a.name,
        agreementId: a.id,
        kind,
        unitPrice: rate !== null && rate !== undefined ? rate.toFixed(2) : '',
      },
    ]);
    // A support invoice is due on receipt (the server defaults the same way
    // when no due date is sent); the form shows it rather than net 30.
    if (kind === 'support') setMeta((m) => ({ ...m, dueDate: m.issueDate }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedClientId, linkedAgreementId, agreementDetail.data]);

  return (
    <Card>
      <CardHeader
        title="Invoice details"
        subtitle="Build the invoice line by line — amounts update as you type."
      />
      <CardBody>
        <div className="mb-4 grid grid-cols-1 gap-4 sm:max-w-xs" data-draft-field="client">
          <Select
            label="Client"
            value={clientId}
            // The message lives ON the control that raised it, not in a
            // footnote a thousand pixels away.
            error={draft.field === 'client' ? (draft.error ?? undefined) : undefined}
            onChange={(e) => {
              setClientId(e.target.value);
              if (draft.field === 'client') draft.setError(null);
            }}
            placeholder="Choose a client…"
            options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>
        <MetaFields meta={meta} onChange={setMeta} subtotal={linesSubtotal(lines)} lines={lines} />
      </CardBody>
      <div className="border-t border-grey-200" data-draft-field="lines">
        <LineItemsEditor
          lines={lines}
          onChange={(next) => {
            setLines(next);
            if (draft.badKeys.size > 0) draft.setBadKeys(new Set());
          }}
          allowAdd
          meta={meta}
          invalidKeys={draft.badKeys}
        />
      </div>
      <CardBody className="border-t border-grey-200 pt-4">
        <div className="flex flex-wrap items-center justify-end gap-3">
          {/* Beside the button, not under the opposite corner of the card. */}
          {draft.error && (
            <p
              role="alert"
              className="mr-auto flex items-center gap-1.5 text-body-sm font-semibold text-error"
            >
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
              {draft.error}
            </p>
          )}
          <Button
            size="lg"
            loading={draft.pending}
            onClick={() => draft.submit({ clientId, lines, meta, fromUnbilled: false })}
          >
            Create draft invoice
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/**
 * The four ways to raise an invoice, in the order Jesse ranked them
 * (2026-09-30): prepaid hours first — most clients buy hours up front and
 * burn them down — then a project's installments, then time & materials, then
 * a hand-built invoice. Every one is addressable — see below. The values of
 * the last two predate the labels ("unbilled", "manual") and stay, because
 * links carry them.
 */
const NEW_INVOICE_TABS = ['prepaid', 'installments', 'unbilled', 'manual'] as const;
type NewInvoiceTab = (typeof NEW_INVOICE_TABS)[number];

export function NewInvoicePage() {
  /**
   * ⚠ **THE TAB IS IN THE URL, because every other tabbed screen in this app
   * is.** `/invoices/new?tab=manual` silently rendered "From unbilled time",
   * which made the manual builder the one destination in the product nobody
   * could link to — not from a doc, not from a chat message, not from the
   * browser's own back button after switching tabs. The coherence rules make a
   * point of this (`?tab=` on /expenses, /budget-forecast, /settings), and the
   * fallback is the same shape they use: an unknown value lands on the default
   * rather than on a blank screen.
   */
  const [params, setParams] = useSearchParams();
  /**
   * Reached from a client (client → agreement → New invoice), the way out is
   * that client, not the portfolio. The id comes off the URL the agreement
   * page built; the NAME comes from the client list this screen already loads,
   * so nothing display-facing has to ride in a query string.
   */
  const from = useReturnOrigin();
  const clientIdParam = params.get('clientId');
  const clientOptions = useInvoiceClients();
  const backClient =
    from === 'client' && clientIdParam
      ? (clientOptions.data?.find((c) => c.id === clientIdParam) ?? null)
      : null;
  const backTo = backClient ? `/clients/${backClient.id}?tab=invoices` : invoiceListHref();
  const backLabel = backClient ? backClient.name : 'Invoices';
  const requested = params.get('tab');
  // An "Invoice $X unbilled" link carries the span it covers; one written
  // before these tabs existed carries no tab, and must still land on the
  // wizard it was built for.
  const tab: NewInvoiceTab = NEW_INVOICE_TABS.includes(requested as NewInvoiceTab)
    ? (requested as NewInvoiceTab)
    : params.get('from') && params.get('to')
      ? 'unbilled'
      : 'prepaid';

  const selectTab = (next: string) => {
    const patched = new URLSearchParams(params);
    patched.set('tab', next);
    // `replace`: switching tabs is not a navigation somebody wants to unwind
    // one step at a time, and a deep link into ?tab=manual must still have
    // /invoices behind it in the history.
    setParams(patched, { replace: true });
  };

  return (
    <div className="mx-auto w-full max-w-6xl">
      <BackLink to={backTo} className="mb-3">
        {backLabel}
      </BackLink>
      <PageHeader
        title="New invoice"
        subtitle="Sell prepaid hours, schedule a project's installments, bill time worked, or build one by hand."
      />
      <Tabs
        value={tab}
        onValueChange={selectTab}
        items={[
          {
            value: 'prepaid',
            label: (
              <span className="inline-flex items-center gap-1.5">
                <Hourglass className="h-4 w-4" aria-hidden />
                Prepaid hours
              </span>
            ),
            content: <PrepaidHoursTab />,
          },
          {
            value: 'installments',
            label: (
              <span className="inline-flex items-center gap-1.5">
                <CalendarRange className="h-4 w-4" aria-hidden />
                Project installments
              </span>
            ),
            content: <InstallmentsTab />,
          },
          {
            value: 'unbilled',
            label: (
              <span className={cn('inline-flex items-center gap-1.5')}>
                <Clock className="h-4 w-4" aria-hidden />
                Time &amp; materials
              </span>
            ),
            content: <FromUnbilledTab />,
          },
          {
            value: 'manual',
            label: (
              <span className="inline-flex items-center gap-1.5">
                <PencilLine className="h-4 w-4" aria-hidden />
                Custom
              </span>
            ),
            content: <ManualTab />,
          },
        ]}
      />
    </div>
  );
}
